import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { LOGO_URL } from "@/utils/constants";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/NotificationBell";
import { HeaderSearch } from "@/components/HeaderSearch";
import { trackHubEvent } from "@/utils/hub";
import { ChevronDown, Menu, Search, X } from "lucide-react";

const BROWSE_ITEMS = [
  ["/travel", "Travel mode", "Browse wellness while traveling"],
  ["/events", "Groups and events", "Meet people nearby"],
  ["/challenges", "Wellness challenges", "Build healthier routines"],
  ["/community", "Community", "Questions and local updates"],
  ["/wellness-hub", "Wellness Hub", "Guides and practical learning"],
  ["/assistants", "AI assistants", "Focused wellness guidance"],
  ["/supplement-analysis", "Supplement analysis", "Review your supplement stack with Maya"],
];

function BrowseMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (event) => { if (ref.current && !ref.current.contains(event.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen((value) => !value)} className="nav-text-link gap-1" data-testid="nav-community-dropdown" aria-expanded={open}>
        Browse <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-3 w-80 bg-white border-2 border-navy/15 shadow-[8px_8px_0_rgba(17,41,107,0.12)] p-2 z-50" data-testid="header-browse-menu">
          {BROWSE_ITEMS.map(([to, label, hint]) => (
            <Link key={to} to={to} onClick={() => setOpen(false)} className="block border-b border-slate-100 px-4 py-3 hover:bg-slate-50 transition-colors last:border-b-0" data-testid={`nav-browse-${label.toLowerCase().replace(/\s+/g, "-")}`}>
              <span className="block text-sm font-body font-semibold text-navy">{label}</span>
              <span className="block text-xs font-body text-gray-500 mt-0.5">{hint}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function AccountMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  if (!user) return <Link to="/login" className="nav-outline-action" data-testid="nav-signin">Sign in</Link>;
  return (
    <div className="relative">
      <button onClick={() => setOpen((value) => !value)} className="nav-outline-action" data-testid="user-menu-btn">Account</button>
      {open && (
        <div className="absolute top-full right-0 mt-3 w-60 bg-white border-2 border-navy/15 shadow-[8px_8px_0_rgba(17,41,107,0.12)] p-2 z-50" data-testid="account-menu-panel">
          <p className="px-3 py-2 text-xs font-body text-gray-500 truncate" data-testid="account-menu-email">{user.email}</p>
          <Link to="/partners/profile" className="header-menu-link" data-testid="menu-my-profile">Partner profile</Link>
          <Link to="/partners/requests" className="header-menu-link" data-testid="menu-my-requests">My requests</Link>
          <Link to="/messages" className="header-menu-link" data-testid="menu-private-messages">Private messages</Link>
          <Link to="/owner" className="header-menu-link" data-testid="menu-owner-portal">Owner portal</Link>
          <button onClick={logout} className="header-menu-link w-full text-left" data-testid="menu-signout">Sign out</button>
        </div>
      )}
    </div>
  );
}

export default function Header({ onSearch, searchQuery = "" }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  return (
    <header className="bg-white/95 backdrop-blur-xl sticky top-0 z-50 border-b border-navy/15 shadow-[0_1px_0_rgba(17,41,107,0.05)]" data-testid="site-header">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:bg-navy focus:text-white focus:px-4 focus:py-2 font-body text-sm" data-testid="skip-to-content">Skip to content</a>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-[60px] md:h-[72px] gap-3 md:gap-4 lg:gap-6">
          <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="header-logo">
            <img src={LOGO_URL} alt="Wellness Collective AI" className="h-9 w-9 md:h-10 md:w-10 object-contain" />
            <span className="hidden sm:block leading-tight max-w-52">
              <span className="block font-heading text-sm font-bold text-navy">Wellness Collective AI</span>
              <span className="block font-mono text-[9px] uppercase text-brand-red">Discover · Connect · Thrive</span>
            </span>
          </Link>

          <div className="hidden md:block flex-1 max-w-md ml-auto"><HeaderSearch onSearch={onSearch} searchQuery={searchQuery} instance="desktop" /></div>

          <nav className="hidden xl:flex items-center gap-5 ml-auto" aria-label="Primary">
            <Link to="/" className="nav-text-link" data-testid="nav-directory">Directory</Link>
            <Link to="/map" className="nav-text-link" data-testid="nav-map">Map</Link>
            <Link to="/wellness-hub" className="nav-text-link" data-testid="nav-wellness-hub">Learn</Link>
            <Link to="/owner" className="nav-text-link" data-testid="nav-owner">For owners</Link>
            <BrowseMenu />
          </nav>

          <div className="hidden lg:flex items-center gap-2 ml-auto xl:ml-0">
            <NotificationBell />
            <Link to="/owner" onClick={() => trackHubEvent("claim_prompt_click", null, { placement: "header" })} className="nav-primary-action" data-testid="nav-claim-venue">Claim a venue</Link>
            <AccountMenu />
          </div>

          <button className="ml-auto md:hidden border border-navy/30 text-navy w-11 h-11 inline-flex items-center justify-center" onClick={() => { setSearchOpen((value) => !value); setMobileOpen(false); }} aria-expanded={searchOpen} aria-label={searchOpen ? "Close search" : "Open search"} data-testid="mobile-search-toggle">
            {searchOpen ? <X className="h-5 w-5" /> : <Search className="h-5 w-5" />}
          </button>
          <button className="md:ml-auto xl:hidden border border-navy/30 text-navy w-11 h-11 inline-flex items-center justify-center" onClick={() => { setMobileOpen((value) => !value); setSearchOpen(false); }} aria-expanded={mobileOpen} aria-label={mobileOpen ? "Close navigation" : "Open navigation"} data-testid="mobile-menu-toggle">
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {searchOpen && <div className="md:hidden absolute inset-x-0 top-full border-b border-navy/15 bg-white px-4 py-3 shadow-[0_8px_16px_rgba(17,41,107,0.08)]" data-testid="mobile-search-panel"><HeaderSearch onSearch={onSearch} searchQuery={searchQuery} instance="mobile" /></div>}

        {mobileOpen && (
          <div className="xl:hidden border-t border-slate-200 py-5" data-testid="mobile-menu-panel">
            <p className="header-menu-label">Find wellness</p>
            <div className="grid grid-cols-2 gap-x-4 mb-5">
              <Link to="/" className="mobile-nav-link" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-directory">Directory</Link>
              <Link to="/map" className="mobile-nav-link" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-map">Map</Link>
              <Link to="/travel" className="mobile-nav-link" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-travel">Travel mode</Link>
              <Link to="/wellness-hub" className="mobile-nav-link" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-wellness-hub">Wellness Hub</Link>
            </div>
            <p className="header-menu-label">Community and support</p>
            <div className="grid grid-cols-2 gap-x-4 mb-5">
              <Link to="/events" className="mobile-nav-link" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-events">Events</Link>
              <Link to="/challenges" className="mobile-nav-link" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-challenges">Challenges</Link>
              <Link to="/community" className="mobile-nav-link" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-community">Community</Link>
              <Link to="/assistants" className="mobile-nav-link" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-assistants">AI assistants</Link>
              <Link to="/supplement-analysis" className="mobile-nav-link" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-supplement-analysis">Supplement analysis</Link>
            </div>
            <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-200">
              <Link to="/owner" className="nav-primary-action" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-owner">Claim a venue</Link>
              <Link to="/login" className="nav-outline-action" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-signin">Sign in</Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}